import React, { useEffect, useMemo, useState } from "react";
import axios from "../../../utills/api";

import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  Badge,
  Spinner,
  FormGroup,
  Label,
  Container,
} from "reactstrap";

import {
  RiHotelLine,
  RiHomeSmileLine,
  RiBrushLine,
  RiCheckboxCircleLine,
  RiUserStarLine,
  RiUserSettingsLine,
  RiSearchEyeLine,
  RiToolsLine,
  RiCloseCircleLine,
  RiLoginCircleLine,
  RiLogoutCircleLine,
  RiTaskLine,
  RiCheckDoubleLine,
  RiTimerLine,
  RiLineChartLine,
  RiHotelBedLine,
} from "react-icons/ri";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import RoomCard from "./RoomCard";
import StaffWorkloadPanel from "./StaffWorkloadPanel";
import SupervisorSummary from "./SupervisorSummary";
import CleaningQueue from "./CleaningQueue";

const HousekeepingDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [rooms, setRooms] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [staff, setStaff] = useState([]);
  const [tasks, setTasks] = useState([]);

  const [search, setSearch] = useState("");
  const [floorFilter, setFloorFilter] = useState("");
  const [buildingFilter, setBuildingFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [housekeeperFilter, setHousekeeperFilter] =
    useState("");
  const [vipOnly, setVipOnly] = useState(false);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);

      const [
        roomsRes,
        bookingsRes,
        staffRes,
        taskRes,
      ] = await Promise.all([
        axios.get("/manage/add-rooms"),
        axios.get("/bookings"),
        axios.get("/team"),
        axios.get("/housekeeping-task"),
      ]);

      setRooms(roomsRes.data || []);
      setBookings(bookingsRes.data || []);
      setStaff(staffRes.data || []);
      setTasks(taskRes.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const today =
    new Date().toISOString().split("T")[0];

  const shift =
    new Date().getHours() < 14
      ? "Morning"
      : new Date().getHours() < 22
        ? "Evening"
        : "Night";

  const housekeepingStaff = useMemo(
    () =>
      staff.filter(
        (s) =>
          s.department?.toLowerCase() ===
          "housekeeping"
      ),
    [staff]
  );

  const dirtyRooms = rooms.filter(
    (r) => r.housekeepingStatus === "DIRTY"
  );

  const cleanRooms = rooms.filter(
    (r) => r.housekeepingStatus === "CLEAN"
  );

  const vacantReady = rooms.filter(
    (r) =>
      r.housekeepingStatus === "CLEAN" &&
      r.occupancyStatus !== "OCCUPIED"
  );

  const occupied = rooms.filter(
    (r) => r.occupancyStatus === "OCCUPIED"
  );

  const cleaning = tasks.filter(
    (t) => t.status === "IN_PROGRESS"
  );

  const inspection = tasks.filter(
    (t) => t.status === "INSPECTION"
  );

  const maintenance = tasks.filter(
    (t) => t.taskType === "MAINTENANCE"
  );

  const outOfOrder = rooms.filter(
    (r) =>
      r.operationalStatus === "OUT_OF_ORDER"
  );

  const vipRooms = rooms.filter(
    (r) => r.isVipRoom
  );

  const todayCheckIns = bookings.filter(
    (b) =>
      b.checkIn &&
      b.checkIn.split("T")[0] === today
  );

  const todayCheckOuts = bookings.filter(
    (b) =>
      b.checkOut &&
      b.checkOut.split("T")[0] === today
  );

  const assignedTasks = tasks.filter(
    (t) =>
      t.status === "ASSIGNED" ||
      t.status === "IN_PROGRESS"
  );

  const completedTasks = tasks.filter(
    (t) => t.status === "COMPLETED"
  );

  const completedWithTime =
    completedTasks.filter(
      (t) => t.startedAt && t.completedAt
    );

  const avgCleaningTime =
    completedWithTime.length > 0
      ? Math.round(
        completedWithTime.reduce(
          (sum, task) =>
            sum +
            (new Date(task.completedAt) -
              new Date(task.startedAt)) /
            60000,
          0
        ) / completedWithTime.length
      )
      : 0;

  const productivity =
    assignedTasks.length + completedTasks.length
      ? Math.round(
        (completedTasks.length /
          (assignedTasks.length +
            completedTasks.length)) *
        100
      )
      : 0;

  const kpis = [
    {
      title: "Dirty Rooms",
      value: dirtyRooms.length,
      icon: "bx bx-brush",
      color: "#dc3545",
      bg: "rgba(220,53,69,.12)",
    },

    {
      title: "Clean Rooms",
      value: cleanRooms.length,
      icon: "bx bx-check-circle",
      color: "#198754",
      bg: "rgba(25,135,84,.12)",
    },

    {
      title: "Vacant Ready",
      value: vacantReady.length,
      icon: "bx bx-home-circle",
      color: "#0d6efd",
      bg: "rgba(13,110,253,.12)",
    },

    {
      title: "Occupied",
      value: occupied.length,
      icon: "bx bx-hotel",
      color: "#6610f2",
      bg: "rgba(102,16,242,.12)",
    },

    {
      title: "Under Cleaning",
      value: cleaning.length,
      icon: "bx bx-loader-circle",
      color: "#fd7e14",
      bg: "rgba(253,126,20,.12)",
    },

    {
      title: "Inspection",
      value: inspection.length,
      icon: "bx bx-search-alt",
      color: "#0dcaf0",
      bg: "rgba(13,202,240,.12)",
    },

    {
      title: "Maintenance",
      value: maintenance.length,
      icon: "bx bx-wrench",
      color: "#6f42c1",
      bg: "rgba(111,66,193,.12)",
    },

    {
      title: "Out Of Order",
      value: outOfOrder.length,
      icon: "bx bx-block",
      color: "#6c757d",
      bg: "rgba(108,117,125,.12)",
    },

    {
      title: "VIP Rooms",
      value: vipRooms.length,
      icon: "bx bx-crown",
      color: "#d63384",
      bg: "rgba(214,51,132,.12)",
    },

    {
      title: "Check-ins",
      value: todayCheckIns.length,
      icon: "bx bx-log-in-circle",
      color: "#198754",
      bg: "rgba(25,135,84,.12)",
    },

    {
      title: "Check-outs",
      value: todayCheckOuts.length,
      icon: "bx bx-log-out-circle",
      color: "#fd7e14",
      bg: "rgba(253,126,20,.12)",
    },

    {
      title: "Housekeepers",
      value: housekeepingStaff.length,
      icon: "bx bx-user-pin",
      color: "#0d6efd",
      bg: "rgba(13,110,253,.12)",
    },

    {
      title: "Assigned Tasks",
      value: assignedTasks.length,
      icon: "bx bx-task",
      color: "#0dcaf0",
      bg: "rgba(13,202,240,.12)",
    },

    {
      title: "Completed Tasks",
      value: completedTasks.length,
      icon: "bx bx-check-double",
      color: "#198754",
      bg: "rgba(25,135,84,.12)",
    },

    {
      title: "Avg Time",
      value: `${avgCleaningTime}m`,
      icon: "bx bx-time-five",
      color: "#6f42c1",
      bg: "rgba(111,66,193,.12)",
    },

    {
      title: "Productivity",
      value: `${productivity}%`,
      icon: "bx bx-line-chart",
      color: "#198754",
      bg: "rgba(25,135,84,.12)",
    },
  ];

  const filteredRooms = rooms.filter(
    (room) => {
      const assignedTask =
        tasks.find(
          (t) =>
            t.roomId === room._id ||
            t.roomId?._id === room._id
        );

      const matchesSearch =
        room.roomNumber
          ?.toString()
          .includes(search);

      const matchesFloor =
        !floorFilter ||
        room.floor?.toString() === floorFilter;

      const matchesBuilding =
        !buildingFilter ||
        room.building === buildingFilter;

      const matchesStatus =
        !statusFilter ||
        room.housekeepingStatus ===
        statusFilter;

      const matchesVip =
        !vipOnly || room.isVipRoom;

      const matchesHousekeeper =
        !housekeeperFilter ||
        assignedTask?.assignedToName ===
        housekeeperFilter;

      return (
        matchesSearch &&
        matchesFloor &&
        matchesBuilding &&
        matchesStatus &&
        matchesVip &&
        matchesHousekeeper
      );
    }
  );

  if (loading) {
    return (
      <div className="page-content text-center mt-5">
        <Spinner />
      </div>
    );
  }
  return (
    <div className="page-content">
      <Container fluid>
        <div>
          <BreadCrumb
            title="Housekeeping Operations Center"
            pageTitle="Housekeeping"
          />
        </div>
        <Card className="mb-4 border-0 shadow-sm">
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">
                Housekeeping Overview
              </h5>
              <Badge color="light" className="text-dark">
                Live Dashboard
              </Badge>
            </div>
            <Row>

              {kpis.map((item, index) => (
                <Col
                  xl={3}
                  lg={4}
                  md={6}
                  sm={6}
                  xs={12}
                  key={index}
                  className="mb-3"
                >
                  <div
                    className="d-flex align-items-center justify-content-between rounded-2 h-100"
                    style={{
                      background: "#ffffff",
                      border: `1px solid ${item.bg}`,
                      padding: "10px"
                    }}
                  >
                    <div className="d-flex align-items-center">

                      <div
                        className="d-flex align-items-center justify-content-center me-3"
                        style={{
                          color: item.color,
                        }}
                      >
                        <i
                          className={item.icon}
                          style={{
                            fontSize: "24px",
                            fontWeight: 600,
                          }}
                        />
                      </div>
                      <div>
                        <div
                          style={{
                            fontSize: "13px",
                            color: "#6B7280",
                            fontWeight: 500,
                          }}
                        >
                          {item.title}
                        </div>
                      </div>
                    </div>
                    <div
                      className="d-flex align-items-center justify-content-center"
                      style={{
                        minWidth: "38px",
                        height: "38px",
                        borderRadius: "8px",
                        background: item.bg,
                        color: item.color,
                        fontWeight: 700,
                        fontSize: "14px",
                      }}
                    >
                      {item.value}
                    </div>

                  </div>

                </Col>
              ))}

            </Row>

          </CardBody>
        </Card>

        <Card className="mb-4">
          <CardBody>

            <Row>

              <Col md={2}>
                <FormGroup>
                  <Label>Search</Label>
                  <Input
                    value={search}
                    onChange={(e) =>
                      setSearch(
                        e.target.value
                      )
                    }
                  />
                </FormGroup>
              </Col>

              <Col md={2}>
                <FormGroup>
                  <Label>Floor</Label>
                  <Input
                    type="select"
                    value={floorFilter}
                    onChange={(e) =>
                      setFloorFilter(
                        e.target.value
                      )
                    }
                  >
                    <option value="">
                      All
                    </option>

                    {[...new Set(
                      rooms.map(
                        (r) => r.floor
                      )
                    )].map((f) => (
                      <option key={f}>
                        {f}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>

              <Col md={2}>
                <FormGroup>
                  <Label>Building</Label>
                  <Input
                    type="select"
                    value={buildingFilter}
                    onChange={(e) =>
                      setBuildingFilter(
                        e.target.value
                      )
                    }
                  >
                    <option value="">
                      All
                    </option>

                    {[...new Set(
                      rooms.map(
                        (r) =>
                          r.building
                      )
                    )].map((b) => (
                      <option key={b}>
                        {b}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>

              <Col md={2}>
                <FormGroup>
                  <Label>Status</Label>
                  <Input
                    type="select"
                    value={statusFilter}
                    onChange={(e) =>
                      setStatusFilter(
                        e.target.value
                      )
                    }
                  >
                    <option value="">
                      All
                    </option>
                    <option>
                      CLEAN
                    </option>
                    <option>
                      DIRTY
                    </option>
                    <option>
                      CLEANING
                    </option>
                    <option>
                      INSPECTION
                    </option>
                  </Input>
                </FormGroup>
              </Col>

              <Col md={2}>
                <FormGroup>
                  <Label>
                    Housekeeper
                  </Label>

                  <Input
                    type="select"
                    value={
                      housekeeperFilter
                    }
                    onChange={(e) =>
                      setHousekeeperFilter(
                        e.target.value
                      )
                    }
                  >
                    <option value="">
                      All
                    </option>

                    {housekeepingStaff.map(
                      (s) => (
                        <option
                          key={s._id}
                        >
                          {s.name}
                        </option>
                      )
                    )}
                  </Input>
                </FormGroup>
              </Col>

              <Col md={2}>
                <FormGroup>
                  <Label>
                    VIP Only
                  </Label>

                  <Input
                    type="checkbox"
                    checked={vipOnly}
                    onChange={(e) =>
                      setVipOnly(
                        e.target.checked
                      )
                    }
                  />
                </FormGroup>
              </Col>

            </Row>

            {/* <div className="d-flex flex-wrap gap-2 mt-2">

              <Button color="primary">
                Assign Task
              </Button>

              <Button color="success">
                Bulk Assign
              </Button>

              <Button color="info">
                Print Worksheet
              </Button>

              <Button color="warning">
                Export
              </Button>

              <Button color="danger">
                Maintenance Ticket
              </Button>

            </div> */}

          </CardBody>
        </Card>

        <Row>
          <Col lg={8}>
            <Row>
              {filteredRooms.map(
                (room) => (
                  <Col
                    lg={6}
                    key={room._id}
                    className="mb-3"
                  >
                    <RoomCard
                      room={room}
                      tasks={tasks}
                      staff={housekeepingStaff}
                      refresh={
                        loadDashboard
                      }
                    />
                  </Col>
                )
              )}

            </Row>
          </Col>
          <Col lg={4}>

            <SupervisorSummary
              rooms={rooms}
              tasks={tasks}
            />

            <StaffWorkloadPanel
              staff={
                housekeepingStaff
              }
              tasks={tasks}
            />

            <CleaningQueue
              rooms={rooms}
              tasks={tasks}
            />

          </Col>

        </Row>
      </Container>
    </div>
  );
};

export default HousekeepingDashboard;